import type { Component } from 'vue'

import type {
  ProductCatalogElement,
  RecommendationElement,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import type { LotteryTeaserProps } from '#product/app/components/LotteryTeaser.vue'

import {
  FreeToPlayTeaser,
  HighFrequencyGameTeaser,
  LotteryTeaser,
  ScratchcardTeaser,
} from '#components'
import { FREE_TO_PLAY_ID } from '#free-to-play/app/constants/constants'
import freeToPlayFallBackground from '~/assets/img/freeToPlay/background-mystery-scratch-fall.webp'
import freeToPlayFallLogo from '~/assets/img/freeToPlay/logo-mystery-scratch-fall.svg'

import HomeTrustTeaserCarousel from '../custom-pages/home/components/HomeTrustTeaserCarousel.vue'

type Element = RecommendationElement | ProductCatalogElement

export type TeaserElementContext = {
  licensedTerritory: string
  locale: string
  onUpdateTeasers?: () => void
  variant?: LotteryTeaserProps['variant']
}

export type ResolvedTeaserElement = {
  component: Component
  key: string
  props: Record<string, unknown>
}

const getElementIdentifier = (element: Element): string => {
  switch (element.__typename) {
    case 'ScratchcardTeaserPayload':
      return element.slug
    case 'FreeToPlayTeaserPayload':
      return FREE_TO_PLAY_ID
    case 'ExploreElement':
    case 'HighFrequencyTeaserPayload':
    case 'LotteryTeaserPayload':
    case 'ReviewElement':
    case 'WinnerTrustElement':
      return element.id
    default:
      return ''
  }
}

export const resolveTeaserElement = (
  element: Element,
  context: TeaserElementContext,
): ResolvedTeaserElement | null => {
  const { licensedTerritory, locale, onUpdateTeasers, variant } = context

  const key = `${element.__typename}_${getElementIdentifier(element)}_${locale}`
  const baseProps = { ...element, licensedTerritory }

  switch (element.__typename) {
    case 'LotteryTeaserPayload':
      return {
        component: LotteryTeaser,
        key,
        props: { ...baseProps, onUpdateTeasers, variant },
      }
    case 'HighFrequencyTeaserPayload':
      return {
        component: HighFrequencyGameTeaser,
        key,
        props: { ...baseProps, onUpdateTeasers, variant },
      }
    case 'ScratchcardTeaserPayload':
      return {
        component: ScratchcardTeaser,
        key,
        props: baseProps,
      }
    case 'FreeToPlayTeaserPayload':
      return {
        component: FreeToPlayTeaser,
        key,
        props: {
          ...baseProps,
          backgroundImage: freeToPlayFallBackground,
          logo: freeToPlayFallLogo,
        },
      }
    case 'WinnerTrustElement':
      return {
        component: HomeTrustTeaserCarousel,
        key,
        props: { ...baseProps, variant: 'winners' },
      }
    case 'ReviewElement':
      return {
        component: HomeTrustTeaserCarousel,
        key,
        props: { ...baseProps, variant: 'reviews' },
      }
    default:
      return null
  }
}

export type MappedTeaserElement = ResolvedTeaserElement & {
  element: RecommendationElement
}

export const mapTeaserElements = (
  elements: Element[],
  context: TeaserElementContext,
): MappedTeaserElement[] =>
  elements
    .map((element) => {
      const resolved = resolveTeaserElement(element, context)
      return resolved && { ...resolved, element }
    })
    .filter((item): item is MappedTeaserElement => item !== null)
